import classNames from 'classnames';
import React, {useState} from 'react';

import {BlogCTAClickEvent, BlogCTAImpressionEvent} from '@udemy/browse-event-tracking';
import {TrackImpression, Tracker} from '@udemy/event-tracking';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {Button} from '@udemy/react-core-components';
import {ContentArea, ContentGrid, ContentGridItem} from '@udemy/react-layout-components';
import {
    CardAuthors,
    CardMediaImage,
    CardTitle,
    CourseProductCard,
    Tag,
} from '@udemy/react-product-card-components';

import styles from './explore-more-section-blog.module.less';

type BlogArticle = {
    title: string;
    url: string;
    author: string;
    thumbnail: string;
    minutesToRead: number;
};

export const ExploreMoreSectionBlog = ({
    articles,
    title,
}: {
    articles: BlogArticle[];
    title: string;
}) => {
    const tempArticles = [...articles];
    const [showMore, setShowMore] = useState(false);
    const isMobile = useMatchMedia('mobile-max');
    const {gettext, interpolate} = useI18n();
    if (!articles.length) {
        return null;
    }

    const getTrackViewFunc = (articleUrl: string) => {
        return () => {
            Tracker.publishEvent(
                new BlogCTAImpressionEvent({
                    url: articleUrl,
                    currentUrl: window.location?.href,
                }),
            );
        };
    };

    const getTrackClickFunc = (articleUrl: string) => {
        return () => {
            Tracker.publishEvent(
                new BlogCTAClickEvent({
                    url: articleUrl,
                    currentUrl: window.location?.href,
                }),
            );
        };
    };

    const renderResources = (showMore: boolean) =>
        tempArticles
            .slice(0, isMobile ? (showMore ? tempArticles.length : 3) : tempArticles.length)
            .map((article, i) => (
                <ContentGridItem defaultSpan={12} mdSpan={4} key={i}>
                    <TrackImpression trackFunc={getTrackViewFunc(article.url)}>
                        <div className={styles['product-card-container']}>
                            <CourseProductCard
                                className={classNames(styles['product-card'])}
                                layout="condensed"
                                title={
                                    <CardTitle
                                        renderLink={CardTitle.renderAnchorTag({
                                            href: article.url,
                                            onClick: getTrackClickFunc(article.url),
                                        })}
                                    >
                                        {article.title}
                                    </CardTitle>
                                }
                                authors={<CardAuthors authors={article.author} />}
                                mediaSquare={
                                    article.thumbnail ? (
                                        <CardMediaImage layout="square" src={article.thumbnail} />
                                    ) : null
                                }
                                mediaWide={
                                    article.thumbnail ? (
                                        <CardMediaImage layout="wide" src={article.thumbnail} />
                                    ) : null
                                }
                                tags={[
                                    <Tag key="article">{gettext('Article')}</Tag>,
                                    <Tag key="tag-2">
                                        {interpolate(gettext('%s min read'), [
                                            article.minutesToRead,
                                        ])}
                                    </Tag>,
                                ]}
                            />
                        </div>
                    </TrackImpression>
                </ContentGridItem>
            ));
    return (
        <section className={classNames(styles['additional-resources-wrapper'], {})}>
            <ContentArea>
                <h2 className={classNames('ud-heading-xxl', styles.heading)}>{title}</h2>
                <div className={classNames(styles.container)}>
                    <ContentGrid>{renderResources(showMore)}</ContentGrid>
                </div>
                {tempArticles.length > 3 && isMobile && (
                    <Button
                        onClick={() => setShowMore(!showMore)}
                        udStyle="secondary"
                        size="medium"
                        className={styles['show-more-button']}
                    >
                        {!showMore
                            ? interpolate(gettext('Show %s more'), [tempArticles.length - 3])
                            : gettext('Show less')}
                    </Button>
                )}
            </ContentArea>
        </section>
    );
};
